import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import getConfig from '../lib/get-config.js';

var _class, _descriptor, _descriptor2, _descriptor3;
/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      service — app-specific ember-intl override: resolves the base
 *              URL and loads per-app translation JSON (Embroider fingerprinted
 *              vs broccoli-asset-rev vs dev) plus the Intl polyfill script.
 *              Owns the "translations could not be loaded" state the app shell
 *              blocks on.
 *   extends:   IntlService (addon: ember-intl/services/intl)
 *   template:  none
 *   key:       getBaseURL, loadTranslations(locale), translationsUnavailable,
 *              failedLocale, retryTranslationsTask, loadPolyfill, t (guarded)
 *   uses:      lib/get-config (getConfig), service:log, global fetch/document
 *              (script inject), ember-concurrency (dropTask)
 *   siblings:  components/bb-translations-unavailable (renders the blocked state)
 *   tests:     intl-test.js
 *   gotchas:   loadTranslations RESOLVES `false` instead of throwing when the
 *              JSON cannot be fetched — throwing killed bootSessionTask and left
 *              every label as a raw key on a still-operable form (Sentry
 *              EMBER-MEDICAL-2TQ). Callers must not cache the locale as loaded
 *              on a `false`. Resource resolution branches on environment +
 *              APP.embroider + rootURL; the classic (non-Embroider) production
 *              path resolves the asset host via ASSET_MAP_ROOT_URLS — an app not
 *              in that set falls back to the plain rootURL path. The unhashed
 *              translation URLs carry `?v=<APP.version>`: S3 serves that JSON
 *              `immutable` for two years, so without it a browser keeps the old
 *              messages while the fingerprinted JS updates (POP-381); an app
 *              whose config carries no version falls back to a per-boot stamp
 *              rather than to an unkeyed URL. `t` is wrapped: a message that
 *              cannot be formatted returns its key instead of throwing through
 *              the render and killing the sub-tree.
 */
// One value per page load, used only when the build config carries no version.
// It is deliberately NOT stable across reloads: an unkeyed URL is what let a
// browser hold two-year-old messages against new code (POP-381), and refetching
// a ~40 KB JSON per boot is the cheaper failure.
const BOOT_STAMP = `boot-${Date.now()}`;

/* istanbul ignore next -- external script-injection glue for the Intl polyfill (§11.4) */
function loadJS(url) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

// Mappa delle lingue per traduzioni e polyfill
/*const TRANSLATIONS = {
  'en-us': 'en-us',
  'en-gb': 'en-gb',
  'fr-fr': 'fr-fr',
  'de-de': 'de-de',
  'it-it': 'it-it',
};*/

// Classic (broccoli-asset-rev) production builds fingerprint the translation
// JSON, so the hashed name has to be read from the per-app assetMap. Only these
// rootURLs deploy one; anything else falls back to the plain rootURL path.
const ASSET_MAP_ROOT_URLS = new Set(['start', 'trail', 'clinic',
// clinic-only-ok: the medical app's rootURL, not copy
'noleggio', 'scuolesci_segreteria', 'scuolesci_maestro', 'scuolesci_ecommerce', 'scuolesci_timbratore', 'sciclub', 'guide_segreteria', 'maisonvive', 'bike_segreteria', 'guidemonterosa_segreteria', 'poppix', 'booking', 'eventicerimoniale']);

// The dominant failure in Sentry EMBER-MEDICAL-2TQ is a bare `Failed to fetch`
// at boot — a transient network drop, not an HTTP error — so a single attempt
// throws away a load that a second try would have completed.
const MAX_FETCH_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 300;

// Statuses worth retrying: a rate limit or a transient upstream/edge failure.
// Every other 4xx is a permanent answer — retrying only delays the fallback.
const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);
let Intl = (_class = class Intl extends IntlService {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "log", _descriptor, this);
    /**
     * True once every source and every retry for a locale has failed. The app
     * shell renders `<BbTranslationsUnavailable />` on this instead of letting
     * the user work a form whose labels are raw translation keys.
     */
    _initializerDefineProperty(this, "translationsUnavailable", _descriptor2, this);
    /** Locale that failed, so the retry button can ask for the same one. */
    _initializerDefineProperty(this, "failedLocale", _descriptor3, this);
    // Once per service instance: a build with no version logs, it does not spam.
    // Not tracked — nothing renders it.
    this._warnedMissingVersion = false;
    /** Overridable in tests so the backoff does not add real seconds to a run. */
    this.retryBaseDelayMs = RETRY_BASE_DELAY_MS;
    /** Keys whose formatting already failed, so the log carries one line each. */
    this._unformattableKeys = new Set();
  }
  getBaseURL() {
    return getConfig(this).environment === 'production' ? getConfig(this)['ember-intl'].assetPath : getConfig(this).rootURL;
  }

  /**
   * Fetch JSON with a bounded exponential backoff.
   *
   * Retries a rejected fetch (offline, DNS, aborted connection — the
   * `Failed to fetch` TypeError that dominates the Sentry issue) and the
   * transient HTTP statuses. A non-retryable status throws immediately so the
   * caller can move on to the next source instead of waiting out the backoff.
   *
   * @param {string} resource - URL to fetch
   * @returns {Promise<object>} parsed JSON
   */
  async _fetchJsonWithRetry(resource) {
    let lastError;
    for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt++) {
      try {
        const response = await fetch(resource);
        if (response.ok) {
          return await response.json();
        }
        const httpError = new Error(`${resource} -> HTTP ${response.status}`);
        httpError.status = response.status;
        if (!RETRYABLE_STATUS.has(response.status)) {
          throw httpError;
        }
        lastError = httpError;
      } catch (err) {
        // A non-retryable HTTP status is final for this source: rethrow it
        // rather than burning the remaining attempts on an answer that will
        // not change.
        if (err?.status && !RETRYABLE_STATUS.has(err.status)) {
          throw err;
        }
        lastError = err;
      }
      if (attempt < MAX_FETCH_ATTEMPTS) {
        await this._backoff(attempt);
      }
    }
    throw lastError;
  }

  /**
   * Exponential backoff with jitter. Jitter matters here because every tab of
   * every tenant boots against the same asset host: a fixed delay would line
   * the retries of a whole fleet up on the same instant.
   *
   * @param {number} attempt - 1-based attempt that just failed
   */
  _backoff(attempt) {
    const delay = this.retryBaseDelayMs * 2 ** (attempt - 1);
    const jitter = delay * 0.25 * Math.random();
    return new Promise(resolve => setTimeout(resolve, delay + jitter));
  }

  /**
   * Ordered list of URLs to try for a locale, most specific first.
   *
   * The second entry is the recovery path: the plain, same-origin rootURL file.
   * The primary URL in production points at the asset host / CDN, so an outage
   * or a DNS failure confined to that host still leaves the app's own origin
   * serving a usable copy. Duplicates are dropped.
   *
   * @param {string} locale - Locale code
   * @returns {Promise<string[]>} candidate resource URLs
   */
  /**
   * Stamp the build version onto a translation URL that carries no fingerprint.
   *
   * The JSON is served from S3 with `Cache-Control: max-age=63072000, immutable`
   * and, under Embroider, at a path with no hash in it — so a browser that has
   * opened the app once keeps that file for two years while the JS bundle, which
   * IS fingerprinted, updates on every release. Rename a placeholder on both
   * sides (POP-189 renamed `{n}` to `{count}`) and the new code then formats the
   * old message: `ts.bulk.selected-n` threw MissingValueError during a re-render
   * and took the whole Sistema TS send bar down with it, on every tenant whose
   * cache predated the release (POP-381).
   *
   * A version in the query string gives each release its own cache entry, which
   * is what the fingerprint does for everything else.
   *
   * @param {string} url - a URL with no content hash in its path
   * @returns {string} the same URL, keyed to this build
   */
  _versioned(url) {
    const version = getConfig(this).APP?.version;

    // No version in the build config (an app without ember-cli-app-version, a
    // trimmed environment.js) used to return the bare URL — silently restoring
    // the exact two-year-stale cache POP-381 was about, with no signal until a
    // tenant reports a feature missing. Fall back to a per-boot stamp instead:
    // one extra fetch per page load, loud in the log, never a stale message.
    if (!version) {
      if (!this._warnedMissingVersion) {
        this._warnedMissingVersion = true;
        this.log?.error?.('intl: APP.version is missing, so translation URLs cannot be keyed to the build. ' + 'Falling back to a per-boot stamp — add ember-cli-app-version to this app (POP-381).');
      }
      return this._stamp(url, BOOT_STAMP);
    }
    return this._stamp(url, version);
  }

  /**
   * @param {string} url - the URL to key
   * @param {string} value - the cache key for this build
   * @returns {string} the URL carrying `?v=<value>`
   */
  _stamp(url, value) {
    return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(value)}`;
  }

  /**
   * A translation that cannot be formatted must never take the page down.
   *
   * `t` runs inside the render, so a throw propagates through Glimmer's
   * `UpdatingVM` and kills the whole sub-tree being rendered — not just the
   * label. That is how a renamed placeholder made the entire Sistema TS send bar
   * disappear: the message in the browser's cached JSON still said `{n}` while
   * the code passed `count`, MissingValueError escaped, and the block that was
   * re-rendering never produced any output at all (POP-381, Sentry
   * EMBER-MEDICAL-2XB / 2XK).
   *
   * The key itself is the fallback: it is what ember-intl already shows for a
   * message it does not have, so a stale cache degrades to an untranslated label
   * instead of a missing feature. Logged once per key — 62 events arrived from a
   * single label before this.
   */
  t(key, ...args) {
    try {
      return super.t(key, ...args);
    } catch (err) {
      if (!this._unformattableKeys.has(key)) {
        this._unformattableKeys.add(key);
        this.log?.warn?.(`intl: "${key}" could not be formatted, showing the key`, err);
      }
      return key;
    }
  }
  async _resolveResources(locale) {
    const filePath = `translations/${locale.toLowerCase()}.json`;
    const config = getConfig(this);
    const plainPath = `${config.rootURL}${filePath}`;
    if (config.environment !== 'production') {
      return [plainPath];
    }
    const rootURL = config.rootURL.replace(/^\/|\/$/g, '');
    const versionedPlainPath = this._versioned(plainPath);
    if (config.APP?.embroider) {
      // Embroider builds are fingerprinted by webpack, not broccoli-asset-rev, so
      // there is no assetMap.json to resolve hashed filenames. Translations are
      // emitted unhashed at dist/translations/<locale>.json and deployed under the
      // same asset host as the rest of the app (mirrors the dist/ structure, e.g.
      // dist/assets/vendor.<hash>.js -> <assetHost>/<rootURL>/assets/vendor.<hash>.js).
      const host = (config.APP.assetHost || '').replace(/\/$/, '');
      return this._dedupe([this._versioned(`${host}/${rootURL}/${filePath}`), versionedPlainPath]);
    }
    const assetMap = ASSET_MAP_ROOT_URLS.has(rootURL) ? await this._loadAssetMap(rootURL) : null;
    if (!assetMap?.assets?.[filePath]) {
      // Unknown rootURL (new app not in the set), an unreachable assetMap, or a
      // 404 that returns HTML: fall back to the plain rootURL path instead of
      // crashing session boot with a TypeError.
      return [versionedPlainPath];
    }
    const prepend = (assetMap.prepend || '').replace(/\/$/, '');

    // The assetMap path already carries a content hash — only the fallback needs
    // the version stamp.
    return this._dedupe([`${prepend}/${rootURL}/${assetMap.assets[filePath]}`, versionedPlainPath]);
  }

  /**
   * The assetMap fetch is on the same flaky boot path as the translations
   * themselves, so it gets the same retry — and it must never throw: before
   * this, a network drop here escaped as an unhandled TypeError before the
   * `response.ok` check could fall back (Sentry EMBER-MEDICAL-2TQ).
   *
   * @param {string} rootURL - rootURL without slashes
   * @returns {Promise<object|null>} the parsed assetMap, or null when unusable
   */
  async _loadAssetMap(rootURL) {
    try {
      return await this._fetchJsonWithRetry(`${rootURL}/assets/assetMap.json`);
    } catch (err) {
      this.log.warn(`loadTranslations: assetMap unavailable for ${rootURL}`, err);
      return null;
    }
  }
  _dedupe(urls) {
    return [...new Set(urls)];
  }

  /**
   * Load and register the translations for a locale.
   *
   * Resolves `false` instead of throwing when nothing could be fetched: the
   * throw used to abort `bootSessionTask` mid-way (Sentry EMBER-MEDICAL-2TQ,
   * `route:null`), which left the user on a fully operable UI whose every label
   * was a raw key — a billing form with unreadable field names and an active
   * save button. The failure is now explicit state (`translationsUnavailable`)
   * that the app shell blocks on, and it is still reported to Sentry.
   *
   * @param {string} locale - Locale code, e.g. `it-IT`
   * @returns {Promise<boolean>} true when translations were registered
   */
  async loadTranslations(locale) {
    let resources;
    try {
      resources = await this._resolveResources(locale);
    } catch (err) {
      // Resource resolution is pure config reading plus the guarded assetMap
      // fetch; a throw here means a malformed config, not a network blip.
      resources = [];
      this.log.error('loadTranslations: could not resolve a translation URL', err);
    }
    let lastError;
    for (const resource of resources) {
      try {
        const translations = await this._fetchJsonWithRetry(resource);
        this.addTranslations(locale, translations);
        this.translationsUnavailable = false;
        this.failedLocale = null;
        return true;
      } catch (err) {
        lastError = err;
      }
    }
    this.failedLocale = locale;
    this.translationsUnavailable = true;
    this.log.error(`loadTranslations: every source failed for "${locale}" (${resources.join(', ')})`, lastError);
    return false;
  }

  /**
   * Retry from the blocking screen. Dropping keeps a repeatedly tapped button
   * from stacking loads; `isRunning` drives the button state.
   */
  *retryTranslationsTask() {
    const locale = this.failedLocale;
    if (!locale) {
      return true;
    }
    const loaded = yield this.loadTranslations(locale);
    if (loaded) {
      this.setLocale(locale);
    }
    return loaded;
  }

  /* istanbul ignore next -- external Intl polyfill script-injection glue (§11.4) */
  async loadPolyfill() {
    await loadJS('/assets/intl/intl.min.js');
  }

  /*async loadPolyfillData(locale) {
    const lang = TRANSLATIONS[locale] || 'it-it';
    const polyfillFile = `assets/intl/locales/${lang}.js`;
     await loadJS(polyfillFile);
  }
   async loadLocale(locale) {
    const promises = [this.loadTranslations(locale)];
     // Carica polyfill solo se necessario
    if (window.Intl === window.IntlPolyfill) {
      promises.push(this.loadPolyfillData(locale));
    }
     await RSVP.all(promises);
  }*/
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "log", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "translationsUnavailable", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "failedLocale", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "retryTranslationsTask", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "retryTranslationsTask"), _class.prototype), _class);

export { Intl as default };
//# sourceMappingURL=intl.js.map
